import { api, ApiError } from "@/lib/api-client";
import type {
  SendOtpPayload,
  VerifyOtpPayload,
  VerifyOtpResponse,
  RegisterPayload,
  AuthTokens,
  CustomerProfile,
  UpdateProfilePayload,
  CheckEmailPayload,
  CheckEmailResponse,
  QuickLoginPayload,
} from "@/types";

export const authService = {
  sendOtp: (payload: SendOtpPayload) =>
    api.post<void>("/auth/send-otp", payload),

  verifyOtp: (payload: VerifyOtpPayload) =>
    (async () => {
      const res = await fetch("/api/auth/verify-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new ApiError(res.status, err.message ?? "Something went wrong", err);
      }
      return (await res.json()) as VerifyOtpResponse;
    })(),

  register: (payload: RegisterPayload, registerToken: string) =>
    (async () => {
      const res = await fetch("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(registerToken ? { Authorization: `Bearer ${registerToken}` } : {}),
        },
        body: JSON.stringify(payload),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new ApiError(res.status, err.message ?? "Something went wrong", err);
      }
      return (await res.json()) as VerifyOtpResponse;
    })(),

  quickLogin: (payload: QuickLoginPayload) =>
    api.post<AuthTokens>("/auth/quick-login", payload),

  getProfile: (token?: string) =>
    token
      ? api.get<CustomerProfile>("/auth/profile", { token })
      : (async () => {
          const res = await fetch("/api/auth/me", { method: "GET", credentials: "include" });
          if (!res.ok) {
            const err = await res.json().catch(() => ({ message: res.statusText }));
            throw new ApiError(res.status, err.message ?? "Something went wrong", err);
          }
          return (await res.json()) as CustomerProfile;
        })(),

  updateProfile: (payload: UpdateProfilePayload, token?: string) =>
    api.patch<CustomerProfile>("/auth/profile", payload, { token }),

  refresh: (token?: string) =>
    api.post<AuthTokens>("/auth/refresh", undefined, { token }),

  logout: () =>
    (async () => {
      const res = await fetch("/api/auth/logout", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new ApiError(res.status, err.message ?? "Something went wrong", err);
      }
      return undefined as void;
    })(),

  checkEmail: (payload: CheckEmailPayload, token?: string) =>
    api.post<CheckEmailResponse>("/auth/check-email", payload, { token }),

  deleteAccount: () =>
    (async () => {
      const res = await fetch("/api/auth/delete-account", {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new ApiError(res.status, err.message ?? "Something went wrong", err);
      }
      return undefined as void;
    })(),

  /**
   * Enregistre la position du client côté session — c'est ELLE qui détermine
   * le catalogue, les frais et le point livré, l'endpoint `/stores` refusant
   * toute coordonnée en paramètre.
   *
   * Renvoie `zone_ulid`, qui vaut `null` hors zone de livraison : le seul
   * signal fiable pour dire au client qu'on ne dessert pas son adresse.
   */
  updateLocation: (payload: { latitude: number; longitude: number }, token?: string) =>
    api.put<{ latitude: number; longitude: number; zone_ulid: string | null }>(
      "/customer/location",
      payload,
      { token },
    ),

  // ── Phone number change ──
  sendPhoneUpdateOtp: (
    payload: { phone: string; type?: "whatsapp" | "sms"; lang?: string },
    token?: string
  ) => api.post<void>("/customer/phone/send-otp", payload, { token }),

  verifyPhoneUpdateOtp: (payload: { phone: string; otp: string }, token?: string) =>
    api.post<void>("/customer/phone/verify-otp", payload, { token }),
};
